import { Input, Radio } from "antd";
import { useEffect, useMemo, useState } from "react";
import { ICardDetails } from "../../types/types";
import ErrorPanel from "../error.panel.component";

export function CardPaymentForm({
  onSuccess,
  onError,
  onChangeAcceptTerms,
  onChangeZipCode,
  context,
  onChangePaymentMode,
  paymentMode,
}: {
  paymentMode?: string;
  context: "customer" | "merchant";
  onChangeAcceptTerms: (value: boolean) => void;
  onChangeZipCode: (value: string) => void;
  onChangePaymentMode?: (value: string) => void;
  onError: (message: string) => void;
  onSuccess: (message: ICardDetails) => void;
}) {
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);

  const termsString = useMemo(() => {
    if (context === "customer") {
      return `I confirm that I am authorized to make this payment and agree that
          this transaction is final and non-refundable except as outlined in the
          merchant’s refund policy. I understand that payment processing fees
          may apply and that disputes must be directed to the merchant listed on
          this invoice.`;
    }
    return `I certify that I am authorized to submit this payment, that the customer has provided consent, and that all applicable processing fees have been clearly disclosed.`;
  }, [context]);

  const [zipCode, setZipCode] = useState<string>("");

  useEffect(() => {
    const handleMessage = (event: MessageEvent<string>) => {
      // 🔐 Validate origin (VERY important in production)
      if (
        event.origin !== "https://fts-uat.cardconnect.com" &&
        event.origin !== "https://fts.cardconnect.com"
      )
        return;

      try {
        const parsed: { message?: string; expiry?: string } = JSON.parse(
          event.data,
        );
        console.log(parsed);

        if (parsed.message) {
          onSuccess({ cardToken: parsed.message, expiry: parsed.expiry });
        }
      } catch (error: any) {
        console.error("Failed to parse token message:", error);
        onError(
          error.message || "An unexpected error occurred in card payment form.",
        );
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const iframeHost = process.env.NODE_ENV === "production" ? "fts" : "fts-uat";

  return (
    <div className="bg-gray-300 px-8 py-10 border border-gray-400 rounded">
      <div className="w-full rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-start gap-3 mb-6 flex flex-col">
        <div className="flex flex-row items-center">
          <img
            src="/assets/icons/shield.png"
            alt="Secure"
            className="inline-block mr-2"
            width={20}
          />
          <p className="text-sm font-semibold text-gray-900">
            Secure Payment Form
          </p>
        </div>

        <p className="text-sm text-gray-600 mt-1 leading-relaxed">
          This card form is securely hosted by our certified payment processor.
          Your card details are encrypted using industry-standard SSL/TLS
          technology and are never stored on our servers.
        </p>
      </div>

      <div className="my-3 mb-5">
        <img src="/assets/card-brands.svg" width={150} alt="major-cards-logo" />
      </div>

      <form name="tokenform" id="tokenform">
        <iframe
          id="tokenframe"
          name="tokenframe"
          src={`https://${iframeHost}.cardconnect.com/itoke/ajax-tokenizer.html?useexpiry=true&usecvv=true&css=body%7Bfont-family%3A-apple-system%2CBlinkMacSystemFont%2C%22Segoe%20UI%22%2CRoboto%2CArial%2Csans-serif%3Bmargin%3A0%3Bpadding%3A0%3B%7Dinput%2Cselect%7Bwidth%3A40%25%3Bheight%3A42px%3Bpadding%3A0%2012px%3Bmargin%3A12px%200%3Bfont-size%3A14px%3Bborder%3A1px%20solid%20%23d1d5db%3Bborder-radius%3A8px%3Bbackground-color%3A%23ffffff%3Bbox-sizing%3Aborder-box%3Btransition%3Aborder-color%200.2s%20ease%2Cbox-shadow%200.2s%20ease%3B%7Dinput%5Bname%3D%22ccnumfield%22%5D%7Bwidth%3A80%25%3B%7Dinput%3Afocus%2Cselect%3Afocus%7Boutline%3Anone%3Bborder-color%3A%232563eb%3Bbox-shadow%3A0%200%200%203px%20rgba%2837%2C99%2C235%2C0.15%29%3B%7Dselect%7Bappearance%3Anone%3Bbackground-color%3A%23ffffff%3B%7D.error%7Bcolor%3A%23dc2626%3Bfont-size%3A13px%3Bmargin-top%3A4px%3B%7D`}
          scrolling="no"
          width="100%"
          height="350"
          title="card-token-frame"
        ></iframe>
        <input type="hidden" name="mytoken" id="mytoken" />
      </form>

      <hr className="border-gray-400" />

      <div className="my-4">
        <div className="flex gap-2 items-center">
          <span className="text-black semi-bold">Billing ZipCode: </span>
          <Input
            placeholder="Zipcode"
            className="w-fit"
            value={zipCode}
            onChange={(e) => {
              setZipCode(e.target.value);
              onChangeZipCode(e.target.value);
            }}
          />
        </div>

        {(!zipCode || (zipCode && isNaN(+zipCode))) && (
          <ErrorPanel message="Please enter valid zipcode" />
        )}
      </div>

      <div className="p-2">
        {context === "merchant" && (
          <Radio.Group
            className="bg-gray-700 px-2 py-1"
            value={paymentMode}
            options={[
              { value: "phone", label: "Phone" },
              { value: "email", label: "Email" },
            ]}
            onChange={(e) => {
              onChangePaymentMode && onChangePaymentMode(e.target.value);
            }}
          />
        )}
      </div>

      <div className="p-2 flex flex-row justify-start items-start">
        <input
          type="checkbox"
          style={{ transform: "scale(1.3)" }}
          id="accept-terms"
          className="mx-2 border mt-1"
          onClick={(e: any) => {
            const val = !acceptTerms;
            setAcceptTerms(val);
            onChangeAcceptTerms(val);
          }}
        />
        <p className="text-gray-700 text-sm">{termsString}</p>
      </div>
    </div>
  );
}
